import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { IoMdDownload } from 'react-icons/io';
import { Button, Text, ActionIcon, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import DeletePopover from '../../../../../components/buttons/DeletePopover';
import FileUpload from '../../../../../components/FileUpload/FileUpload';
import ModalForm from '../../../../../components/forms/ModalForm';
import useHandleDownloadDocument from '../../../../../hooks/useDownloadDocument';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { ClientService } from '../../../../../services/client/clientService';
import { DocumentsService } from '../../../../../services/document/documentsService';

interface CreditUpdateDocumentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    creditUpdate: any | null;
    onDocumentsChanged: () => Promise<void> | void;
}

interface CreditUpdateDocument {
    id: string;
    originalName: string;
    extension: string;
    createdAt: string | null;
}

const LAST_CREDIT_DOCUMENT_DELETE_TOOLTIP = 'Debe permanecer al menos un documento de crédito';

const CreditUpdateDocumentsModal: React.FC<CreditUpdateDocumentsModalProps> = ({
    isOpen,
    onClose,
    creditUpdate,
    onDocumentsChanged,
}) => {
    const clientService = new ClientService();
    const documentService = new DocumentsService();
    const { handleDownloadDocumentNewTab } = useHandleDownloadDocument();
    const { handleErrors } = useHandleErrors();

    const [files, setFiles] = useState<File[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const documents = useMemo<CreditUpdateDocument[]>(() => {
        if (!Array.isArray(creditUpdate?.documents)) {
            return [];
        }

        return creditUpdate.documents
            .map((relation: any) => {
                const doc = relation?.document ?? relation;

                if (!doc?.id) {
                    return null;
                }

                return {
                    id: doc.id,
                    originalName: doc.originalName ?? doc.fileName ?? 'Documento',
                    extension: doc.extension ?? '',
                    createdAt: relation?.createdAt?.date
                        || doc?.createdAt?.date
                        || relation?.createdAt
                        || doc?.createdAt
                        || null,
                };
            })
            .filter((document: CreditUpdateDocument | null): document is CreditUpdateDocument => Boolean(document));
    }, [creditUpdate]);

    const uploadDocuments = async () => {
        if (!creditUpdate?.id) {
            toast.error('No se ha seleccionado una actualización de créditos');
            return;
        }

        if (!files.length) {
            toast.error('Selecciona al menos un documento para subir');
            return;
        }

        setIsUploading(true);
        try {
            const response = await (await clientService.addCreditsUpdateDocuments(creditUpdate.id, files)).getResponseData();

            if (response?.success) {
                toast.success('Documentos subidos correctamente');
                setFiles([]);
                await onDocumentsChanged();
            } else {
                toast.error(response?.message || 'No se pudieron subir los documentos');
            }
        } catch (error: any) {
            toast.error(error?.message || 'No se pudieron subir los documentos');
        } finally {
            setIsUploading(false);
        }
    };

    const deleteDocument = async (documentId: string) => {
        if (!creditUpdate?.id) {
            toast.error('No se ha seleccionado una actualización de créditos');
            return;
        }

        if (documents.length <= 1) {
            toast.error(LAST_CREDIT_DOCUMENT_DELETE_TOOLTIP);
            return;
        }

        setIsDeleting(true);
        try {
            const response = await (
                await clientService.deleteCreditsUpdateDocument(creditUpdate.id, documentId)
            ).getResponseData();

            if (response?.success) {
                toast.success('Documento eliminado correctamente');
                await onDocumentsChanged();
            } else {
                handleErrors(response);
            }
        } catch (error: any) {
            toast.error(error?.message || 'No se pudo eliminar el documento');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <ModalForm
            isOpen={isOpen}
            onClose={onClose}
            title='Documentos de actualización de créditos'
            size='xl'
        >
            <div className='pb-6 space-y-6 overflow-y-auto'>
                <FileUpload
                    files={files}
                    onFilesChange={setFiles}
                    title='Adjuntar documentos'
                    dropText='Arrastra archivos aquí o haz clic para seleccionarlos'
                    dragActiveText='Suelta los archivos aquí...'
                    maxFiles={50}
                    maxSize={52428800}
                    showFileList
                />

                <div className='flex justify-end'>
                    <Button
                        color='primary'
                        onClick={uploadDocuments}
                        isLoading={isUploading}
                        disabled={isUploading || isDeleting || files.length === 0}
                    >
                        Subir documentos
                    </Button>
                </div>

                <div className='space-y-3'>
                    <Text className='text-sm font-medium text-gray-700'>
                        Documentos subidos ({documents.length})
                    </Text>

                    {documents.length === 0 && (
                        <div className='rounded-lg border border-dashed border-gray-300 p-4 text-sm text-gray-500'>
                            Esta actualización de créditos todavía no tiene documentos adjuntos.
                        </div>
                    )}

                    {documents.length > 0 && (
                        <div className='space-y-2'>
                            {documents.map((document) => (
                                <div
                                    key={document.id}
                                    className='flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2'
                                >
                                    <div className='min-w-0'>
                                        <Text className='truncate text-sm font-medium text-gray-800'>
                                            {document.originalName}
                                        </Text>
                                        <Text className='text-xs text-gray-500'>
                                            {document.createdAt && moment(document.createdAt).isValid()
                                                ? moment(document.createdAt).format('DD/MM/YYYY HH:mm')
                                                : 'Fecha no disponible'}
                                        </Text>
                                    </div>

                                    <div className='ml-4 flex items-center gap-2'>
                                        <Tooltip
                                            size='sm'
                                            content='Descargar documento'
                                            placement='top'
                                            color='invert'
                                        >
                                            <ActionIcon
                                                as='span'
                                                size='sm'
                                                variant='outline'
                                                className='cursor-pointer hover:!border-gray-900 hover:text-gray-700'
                                                onClick={() => handleDownloadDocumentNewTab(
                                                    documentService.downloadDocument,
                                                    document.id,
                                                    document.originalName,
                                                    document.extension
                                                )}
                                            >
                                                <IoMdDownload className='h-4 w-4' size={20} />
                                            </ActionIcon>
                                        </Tooltip>

                                        {documents.length > 1 ? (
                                            <DeletePopover
                                                title='Eliminar documento'
                                                description={`¿Deseas eliminar ${document.originalName}?`}
                                                onDelete={() => deleteDocument(document.id)}
                                            />
                                        ) : (
                                            <Tooltip
                                                size='sm'
                                                content={LAST_CREDIT_DOCUMENT_DELETE_TOOLTIP}
                                                placement='top'
                                                color='invert'
                                            >
                                                <span className='inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-400'>
                                                    —
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </ModalForm>
    );
};

export default CreditUpdateDocumentsModal;
